import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonProps, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { CSSProperties, FC } from 'react';

import { useStyles } from './styles';

export type BaseButtonProps = {
  text?: string,
  ariaLabel?: string,
  iconOnly?: boolean,
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
  isLoading?: boolean,
  disableElevation?: boolean,
  backgroundColor?: CSSProperties['backgroundColor'],
  hoverColor?: CSSProperties['backgroundColor'],
  disabled?: boolean,
  icon?: IconProp,
  size?: 'small' | 'medium' | 'large',
  type?: 'submit' | 'button' | 'reset',
  iconColor?: string,
  buttonVariant?: 'text' | 'outlined' | 'contained',
  buttonColor?: ButtonProps['color'],
  textColor?: CSSProperties['color'],
  borderColor?: CSSProperties['backgroundColor'],
  width?: CSSProperties['width'],
  height?: CSSProperties['height'],
  customStyle?: React.CSSProperties | undefined,
  elementId?: string,
  form?: string
}

export const BaseButton: FC<BaseButtonProps> = ({
  text,
  ariaLabel,
  iconOnly = false,
  icon,
  backgroundColor,
  onClick = () => { },
  isLoading = false,
  disableElevation = true,
  disabled = false,
  size = 'medium',
  type = 'button',
  iconColor,
  hoverColor,
  buttonVariant = 'contained',
  buttonColor = 'secondary',
  textColor,
  borderColor,
  width,
  height,
  customStyle,
  elementId,
  form
}) => {
  const theme = useTheme();
  const isNeutralOutlined = buttonColor === 'inherit' && buttonVariant !== 'contained';
  const isDisabledOnDark = disabled && theme.palette.mode === 'dark';
  const labelColor = (() => {
    if (textColor) return textColor;
    if (isDisabledOnDark) return theme.palette.text.secondary;
    return isNeutralOutlined ? 'inherit' : undefined;
  })();
  const classes = useStyles({
    buttonBackgroundColor: backgroundColor,
    buttonHoverColor: hoverColor,
    textColor: labelColor,
    buttonBorderColor: borderColor,
    width,
    height,
    iconOnly
  });

  return (
    <Button
      id={elementId}
      className={classes.button}
      variant={buttonVariant}
      disableElevation={disableElevation}
      color={buttonColor}
      disabled={disabled}
      onClick={onClick}
      size={size}
      type={type}
      form={form}
      style={customStyle}
      aria-label={ariaLabel}
    >
      <Typography variant="body2" className={classes.text}>
        {!isLoading && icon && (
          <FontAwesomeIcon
            icon={icon}
            style={{ marginRight: text ? undefined : 0 }}
            className={iconOnly ? classes.iconOnly : classes.icon}
            color={iconColor}
          />
        )}

        {isLoading && <FontAwesomeIcon spin icon="spinner" />}
        {!isLoading && text}
      </Typography>
    </Button>
  );
};